import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { TextInput } from '@/components/ui/text-input';
import { toast } from '@/components/ui/toast';
import { SocialSignInButtons } from '@/features/auth/social-sign-in-buttons';
import { useAuth } from '@/hooks/use-auth';
import { validateEmail } from '@/utils/auth-validation';
import { createLogger } from '@/utils/logger';
import { router } from 'expo-router';
import { useRef, useState } from 'react';
import { View, type TextInput as RNTextInput } from 'react-native';
import { AuthLink } from './components/auth-link';
import { AuthScreenLayout } from './components/auth-screen-layout';

const logger = createLogger('LOGIN');

export function LoginScreen() {
  const { login, sendVerificationOTP } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const passwordRef = useRef<RNTextInput>(null);

  const validateInputs = () => {
    const newErrors: Record<string, string> = {};

    const emailError = validateEmail(email);
    if (emailError) newErrors.email = emailError;

    if (!password) newErrors.password = 'Password is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async () => {
    if (!validateInputs()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const result = await login(email.trim(), password);

      if (result.error) {
        const isVerificationError = result.error.status === 403;

        if (isVerificationError) {
          logger.log('Email verification required, redirecting to OTP page');
          try {
            await sendVerificationOTP(email.trim());
          } catch {
            logger.error('OTP sending error');
          }
          router.replace({ pathname: '/(auth)/verify-otp', params: { email: email.trim() } });
        } else {
          logger.error('Login failed:', result.error);
          // Not attributable to a field: deliberately vague for credentials, and it
          // also covers network failure, so it must not accuse the email address.
          toast.error('Could not log you in', {
            description: 'Check your email and password, then try again.',
          });
        }
      }
    } catch {
      toast.error('Something went wrong', {
        description: 'Check your connection and try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthScreenLayout
      title="Log In"
      logo="full"
      heading="Welcome back"
      footer={
        <View className="flex-row justify-center items-center gap-1">
          <Text className="text-sm text-warm-gray-300">Don&apos;t have an account?</Text>
          <AuthLink
            onPress={() => router.push({ pathname: '/(auth)/register' })}
            testID="login-signup-link"
          >
            Sign up
          </AuthLink>
        </View>
      }
      socialSlot={<SocialSignInButtons context="login" />}
    >
      <View className="gap-4">
        <TextInput
          placeholder="Email"
          value={email}
          onChangeText={(text) => {
            setEmail(text);
            if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
          }}
          autoCapitalize="none"
          autoComplete="email"
          textContentType="emailAddress"
          keyboardType="email-address"
          returnKeyType="next"
          onSubmitEditing={() => passwordRef.current?.focus()}
          submitBehavior="submit"
          error={errors.email || undefined}
        />

        <TextInput
          ref={passwordRef}
          placeholder="Password"
          value={password}
          onChangeText={(text) => {
            setPassword(text);
            if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
          }}
          secureTextEntry
          autoCapitalize="none"
          autoCorrect={false}
          spellCheck={false}
          autoComplete="current-password"
          textContentType="password"
          returnKeyType="go"
          onSubmitEditing={handleLogin}
          error={errors.password || undefined}
        />

        <View className="flex-row justify-end">
          <AuthLink
            tone="muted"
            onPress={() => router.push({ pathname: '/(auth)/forgot-password' })}
            testID="login-forgot-password"
          >
            Forgot password?
          </AuthLink>
        </View>

        <Button
          size="lg"
          fullWidth
          className="mt-2"
          onPress={handleLogin}
          loading={isLoading}
          testID="login-submit"
        >
          Log in
        </Button>
      </View>
    </AuthScreenLayout>
  );
}
